import { getOrdinal } from "@circle-react/helpers/localeHelpers";
import { PRICE_SCENARIOS } from "@circle-react/helpers/paywallPriceHelpers";
import type { PaywallCheckoutPreview } from "@circle-react/types";
import { compact } from "lodash";

import { t } from "@/i18n-js/instance";

import type { CheckoutPreviewEntryProps } from "../../../components/PreviewEntry";
import { Lines } from "../Lines";
import type { ComponentLineProps } from "../types";
import { shouldExpandFirstPayment } from "../variants/helpers";

const todaysPaymentLocale = "paywall_checkout.todays_payment";

const buildOptions = (
  preview: PaywallCheckoutPreview,
): Partial<CheckoutPreviewEntryProps> => {
  const withValue: Partial<CheckoutPreviewEntryProps> = {
    label: t(todaysPaymentLocale),
    value: preview.formatted_amount_due_now,
    valueWeight: "semibold",
  };
  const withoutValue: Partial<CheckoutPreviewEntryProps> = {
    label: t(todaysPaymentLocale),
    value: undefined,
  };

  if (
    [
      PRICE_SCENARIOS.trialThenValue,
      PRICE_SCENARIOS.trialThenUpfrontThenValue,
    ].includes(preview.preview_scenario)
  ) {
    return withoutValue;
  }

  if (
    [
      PRICE_SCENARIOS.upfrontThenValue,
      PRICE_SCENARIOS.upfrontThenTrialThenValue,
    ].includes(preview.preview_scenario)
  ) {
    if (shouldExpandFirstPayment(preview) || !preview?.trial_period?.paid) {
      return withValue;
    }

    return withoutValue;
  }

  if ([PRICE_SCENARIOS.value].includes(preview.preview_scenario)) {
    const numberOfInstallments =
      1 +
      (preview.future_payments ? 1 : 0) +
      (preview?.remaining_payments?.installments_preview.length || 0);

    const labelString = compact([
      numberOfInstallments > 1
        ? t("paywall_checkout.first_payment.header_installments")
        : t("paywall_checkout.todays_payment"),
      numberOfInstallments > 1
        ? `(${t("progress_counter", { count: getOrdinal(1), total_count: numberOfInstallments })})`
        : null,
    ]).join(" ");

    return { ...withValue, label: labelString };
  }

  return withValue;
};

export const TodayPaymentLine = ({ preview }: ComponentLineProps) => {
  const options = buildOptions(preview);
  return <Lines.TodayPaymentLine preview={preview} options={options} />;
};
